import { useEffect, useState } from "react"
import { visuallyHidden } from '@mui/utils';
import { PostFormDataToBackoffice } from "../../utils/commands";
import path from "path";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { Close, Replay } from "@mui/icons-material";
import theme from "../../theme";

export type UploadStatus = "uploading" | "success" | "error";

type FileWithBlob = {
  file: File,
  blobId?: string,
  status: UploadStatus
}

type FileUploaderProps = {
    urlCatalog: string;
    accept?: string;
    onUpload: (blobId: string) => void,
    onRemove: (blobId: string) => void,
    clearFiles: boolean,
    inputRef: React.Ref<HTMLInputElement>;
}

export default function FileUpload({urlCatalog, accept, inputRef, onUpload, onRemove, clearFiles}: FileUploaderProps) {
  const [files, setFiles] = useState<FileWithBlob[]>([])

  useEffect(() => {
    if(clearFiles){
      setFiles([])
    }
  }, [clearFiles])

  const handleFile = async (fileInput: File) => {  
    const formData = new FormData()
    const file: FileWithBlob = { file: fileInput, status: "uploading" }
    setFiles((prev) => [...prev, file])
    formData.append("form", fileInput)

    try {
      const res = await PostFormDataToBackoffice(`/upload/${urlCatalog}/file`, formData);
      setFiles(prev => prev.map(f => f === file ? { ...f, blobId: res.blobId, status: "success" } : f))
      onUpload(res.blobId);
    } catch (err) {
      console.error("Upload failed", err);
      setFiles(prev => prev.map(f => f === file ? { ...f, status: "error" } : f))
    }
  }

  return(
    <>
    {
      files.map((file) => (
        <>
        <FileUploadBar 
          file={file.file} 
          status={file.status} 
          onRemove={() => {
            if(file.blobId){
              onRemove(file.blobId)
            }
            setFiles(prev => prev.filter(f => f !== file))
          }}
          onRetry={handleFile}
          />
          </>
        ))
      }
      <input 
        type="file"
        onChange={(e) => e.target.files ? handleFile(e.target.files[0]) : null} //will only be one element since multiple is not enabled
        style={visuallyHidden}
        accept={accept}
        ref={inputRef}
      />
    </>

  )
}


const FileUploadBar = ({file, status, onRetry, onRemove}:{file:File, status: UploadStatus, onRetry: (file: File) => void, onRemove: () => void,}) => {
  return (
    <Box sx={{
      backgroundColor: status === "uploading" ? theme.palette.primary.light : (status === "error" ?  theme.palette.error.light : theme.palette.primary.light),
      paddingLeft: "10px", 
      marginY: "5px", 
      borderRadius: "5px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      width: "60%",
      height: "2rem",
      }}>
      <Box sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width:"70%"
        }}>
        {
          status === "uploading" && (
            <CircularProgress style={{ marginRight: 10 }} size={15} color='primary' />
          )
        }
        <Tooltip title={file.name}>
          <Typography sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
            {status === "uploading" ? "Uploading file..." : (status === "error" ?  "Failed to upload" : file.name) }
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{display:'flex', flexDirection:'row'}}>
        {
          status === "error" && (
            <IconButton onClick={() => {
              onRetry(file)
              onRemove()
              }}>
              <Replay/>
            </IconButton>
          )
        }
        <IconButton onClick={() => onRemove()}>
          <Close />
        </IconButton>
      </Box>
    </Box>
  )
}

